import * as React from 'react';
import { Modal, Form, Input, InputNumber, Select, AutoComplete, Button, message, Spin } from 'antd';
import { getSuppliers, createSupplier } from '../../../../../HttpRequests/suppliers';
import { createSupplierItem } from '../../../../../HttpRequests/supplierItems';

export default class SaveToCatalogModal extends React.PureComponent {
  state = {
    submitting: false,
    supplierSearch: '',
    supplierOptions: [],
    selectedSupplierId: null,
    loadingSuppliers: false,
    fields: {
      item_name: '',
      item_code: '',
      description: '',
      unit_of_measure: '',
      unit_price: '',
      tax_rate: 20,
      category: '',
    },
  }

  componentDidMount() {
    const { item } = this.props;
    this.setState({
      supplierSearch: item.supplier_name || '',
      fields: {
        item_name: item.item_name || '',
        item_code: item.item_code || '',
        description: item.description || '',
        unit_of_measure: item.unit_of_measure || 'Item',
        unit_price: item.unit_cost || '',
        tax_rate: item.tax_rate !== undefined && item.tax_rate !== null ? parseInt(item.tax_rate) : 20,
        category: '',
      },
    });
    if (item.supplier_name) {
      this.searchSuppliers(item.supplier_name);
    }
  }

  searchSuppliers = (search) => {
    if (!search) {
      this.setState({ supplierOptions: [], selectedSupplierId: null });
      return;
    }
    this.setState({ loadingSuppliers: true });
    getSuppliers({ params: { search, per_page: 20 } })
      .then(response => {
        const suppliers = (response.data.data || response.data || []);
        const options = suppliers.map(s => ({
          value: s.company_name,
          label: s.company_name,
          id: s.id,
        }));
        this.setState({ supplierOptions: options, loadingSuppliers: false });
        // Auto-select if exact match found (e.g. pre-fill from item)
        const match = suppliers.find(s => s.company_name === search);
        if (match) {
          this.setState({ selectedSupplierId: match.id });
        }
      })
      .catch(() => this.setState({ loadingSuppliers: false }));
  }

  onSupplierChange = (value) => {
    this.setState({ supplierSearch: value, selectedSupplierId: null });
    if (this.supplierTimer) clearTimeout(this.supplierTimer);
    this.supplierTimer = setTimeout(() => this.searchSuppliers(value), 300);
  }

  onSupplierSelect = (value, option) => {
    this.setState({ supplierSearch: value, selectedSupplierId: option.id });
  }

  onChangeField = (field, value) => {
    this.setState(prev => ({ fields: { ...prev.fields, [field]: value } }));
  }

  onSubmit = async () => {
    const { onClose } = this.props;
    const { supplierSearch, selectedSupplierId, fields } = this.state;

    if (!supplierSearch.trim()) {
      message.error('Please enter a supplier name.');
      return;
    }
    if (!fields.item_name.trim()) {
      message.error('Please enter an item name.');
      return;
    }

    this.setState({ submitting: true });

    try {
      let supplierId = selectedSupplierId;

      if (!supplierId) {
        // Create new supplier
        const resp = await createSupplier({ data: { company_name: supplierSearch.trim() } });
        supplierId = (resp.data.data || resp.data).id;
      }

      await createSupplierItem({
        data: {
          supplier_id: supplierId,
          name: fields.item_name,
          item_code: fields.item_code || null,
          description: fields.description || null,
          unit_of_measure: fields.unit_of_measure || 'Item',
          unit_price: fields.unit_price || 0,
          currency: 'GBP',
          tax_rate: fields.tax_rate,
          category: fields.category || null,
          active: true,
        },
      });

      message.success('Item saved to catalog successfully.');
      onClose();
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.message) || 'Failed to save item to catalog.';
      message.error(msg);
    } finally {
      this.setState({ submitting: false });
    }
  }

  render() {
    const { onClose } = this.props;
    const { submitting, supplierSearch, supplierOptions, loadingSuppliers, fields } = this.state;

    return (
      <Modal
        title="Save to Catalog"
        visible
        onCancel={onClose}
        width={600}
        footer={[
          <Button key="cancel" onClick={onClose}>Cancel</Button>,
          <Button key="save" type="primary" loading={submitting} onClick={this.onSubmit}>Save to Catalog</Button>,
        ]}
      >
        <Form layout="vertical">

          <Form.Item label="Supplier" required>
            <Spin spinning={loadingSuppliers} size="small">
              <AutoComplete
                value={supplierSearch}
                options={supplierOptions}
                onChange={this.onSupplierChange}
                onSelect={this.onSupplierSelect}
                placeholder="Search existing supplier or type new name..."
                style={{ width: '100%' }}
                allowClear
              />
            </Spin>
            {!this.state.selectedSupplierId && supplierSearch.trim() && (
              <div style={{ fontSize: 12, color: '#faad14', marginTop: 4 }}>
                No existing supplier matched — a new supplier will be created.
              </div>
            )}
          </Form.Item>

          <Form.Item label="Item Name" required>
            <Input
              value={fields.item_name}
              onChange={e => this.onChangeField('item_name', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Item Code">
            <Input
              value={fields.item_code}
              onChange={e => this.onChangeField('item_code', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Description">
            <Input.TextArea
              value={fields.description}
              onChange={e => this.onChangeField('description', e.target.value)}
              rows={2}
            />
          </Form.Item>

          <Form.Item label="Category">
            <Select
              value={fields.category || undefined}
              onChange={v => this.onChangeField('category', v)}
              placeholder="Select category"
              showSearch
              allowClear
              style={{ width: '100%' }}
            >
              <Select.OptGroup label="Materials">
                <Select.Option value="Paint & Coatings">Paint &amp; Coatings</Select.Option>
                <Select.Option value="Plumbing Materials">Plumbing Materials</Select.Option>
                <Select.Option value="Electrical Materials">Electrical Materials</Select.Option>
                <Select.Option value="Timber & Joinery">Timber &amp; Joinery</Select.Option>
                <Select.Option value="Bricks & Blocks">Bricks &amp; Blocks</Select.Option>
                <Select.Option value="Cement & Aggregates">Cement &amp; Aggregates</Select.Option>
                <Select.Option value="Roofing Materials">Roofing Materials</Select.Option>
                <Select.Option value="Insulation">Insulation</Select.Option>
                <Select.Option value="Flooring Materials">Flooring Materials</Select.Option>
                <Select.Option value="Tiles & Ceramics">Tiles &amp; Ceramics</Select.Option>
                <Select.Option value="Ironmongery">Ironmongery (Hardware)</Select.Option>
                <Select.Option value="Glass & Glazing">Glass &amp; Glazing</Select.Option>
                <Select.Option value="Drywall & Plaster">Drywall &amp; Plaster</Select.Option>
                <Select.Option value="Adhesives & Sealants">Adhesives &amp; Sealants</Select.Option>
              </Select.OptGroup>
              <Select.OptGroup label="Equipment Hire">
                <Select.Option value="Plant Hire">Plant Hire (Excavators, Dumpers)</Select.Option>
                <Select.Option value="Access Equipment">Access Equipment (Scaffolding, Towers, Lifts)</Select.Option>
                <Select.Option value="Power Tools">Power Tools</Select.Option>
                <Select.Option value="Hand Tools">Hand Tools</Select.Option>
                <Select.Option value="Temporary Works Equipment">Temporary Works Equipment</Select.Option>
                <Select.Option value="Site Accommodation">Site Accommodation (Cabins, Toilets)</Select.Option>
              </Select.OptGroup>
              <Select.OptGroup label="Services">
                <Select.Option value="Subcontractor Labour">Subcontractor Labour</Select.Option>
                <Select.Option value="Professional Services">Professional Services (Surveying, Testing)</Select.Option>
                <Select.Option value="Waste Disposal">Waste Disposal</Select.Option>
                <Select.Option value="Site Security">Site Security</Select.Option>
                <Select.Option value="Cleaning Services">Cleaning Services</Select.Option>
              </Select.OptGroup>
              <Select.OptGroup label="Consumables">
                <Select.Option value="PPE">PPE (Personal Protective Equipment)</Select.Option>
                <Select.Option value="Site Consumables">Site Consumables (Fuel, Cleaning)</Select.Option>
                <Select.Option value="Office Supplies">Office Supplies</Select.Option>
              </Select.OptGroup>
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item label="Unit of Measure" style={{ flex: 1 }}>
              <Input
                value={fields.unit_of_measure}
                onChange={e => this.onChangeField('unit_of_measure', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Unit Price (£)" style={{ flex: 1 }}>
              <InputNumber
                value={fields.unit_price}
                onChange={v => this.onChangeField('unit_price', v)}
                min={0}
                step={0.01}
                precision={2}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item label="VAT Rate (%)" style={{ flex: 1 }}>
              <Select
                value={fields.tax_rate !== undefined && fields.tax_rate !== null ? parseInt(fields.tax_rate) : 20}
                onChange={v => this.onChangeField('tax_rate', v)}
                style={{ width: '100%' }}
              >
                <Select.Option value={0}>0%</Select.Option>
                <Select.Option value={20}>20%</Select.Option>
              </Select>
            </Form.Item>

          </div>

        </Form>
      </Modal>
    );
  }
}
